import { PersistedWorkflow } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';

type BucketFlushReason = PersistedWorkflow.BucketFlushReason;

/**
 * A bucket flush, read back off the step payload.
 *
 * The envelope is built server-side in `buildBucketFlushEnvelope` and lands in
 * the step's input and output untouched, so this only has to read it — but it
 * has to read it defensively. The payload is whatever was stored when the run
 * happened, which may predate any field added since, and a step view that
 * throws on an old run is worse than one that shows JSON.
 */
export type BucketFlushEnvelopeView = {
  readonly count: number;
  readonly reason: BucketFlushReason;
  /** Oldest claimed contribution; null when the flush claimed nothing. */
  readonly from: string | null;
  readonly to: string;
  readonly flushedAt: string;
  /** Reducer outputs, in the order they were written. */
  readonly reduced: readonly {
    readonly name: string;
    readonly value: unknown;
  }[];
  readonly items: readonly unknown[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isReason = (value: unknown): value is BucketFlushReason =>
  value === 'count' || value === 'time' || value === 'manual';

/**
 * Reads a flush envelope, or null when the value is not one.
 *
 * Null is the signal to fall back to the plain JSON view: an accumulate step
 * holds one run's contribution, not an envelope, and the same view renders
 * both.
 */
export function readBucketFlushEnvelope(
  value: unknown
): BucketFlushEnvelopeView | null {
  if (!isRecord(value) || !Array.isArray(value['items'])) {
    return null;
  }
  const window = value['window'];
  if (!isRecord(window) || typeof window['to'] !== 'string') {
    return null;
  }
  const reduced = value['reduced'];
  const items = value['items'] as readonly unknown[];
  return {
    // `count` is the envelope's own field, but `items.length` is the thing on
    // screen — trusting the field would let a stale count contradict the list
    // directly beneath it.
    count: items.length,
    reason: isReason(window['reason']) ? window['reason'] : 'count',
    from: typeof window['from'] === 'string' ? window['from'] : null,
    to: window['to'],
    flushedAt:
      typeof window['flushedAt'] === 'string'
        ? window['flushedAt']
        : window['to'],
    reduced: isRecord(reduced)
      ? Object.entries(reduced).map(([name, entry]) => ({ name, value: entry }))
      : [],
    items,
  };
}

/** Why this flush ran, in one clause that completes "Flushed …". */
export function describeFlushReason(reason: BucketFlushReason): string {
  switch (reason) {
    case 'count':
      return 'because enough items had collected';
    case 'time':
      return 'on schedule';
    case 'group':
      return 'because the batch feeding it finished';
    case 'manual':
      return 'by hand';
    default:
      return Assert.unreachable(reason);
  }
}
